import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForecastingView = ({ formData, setFormData, selectedPlants = [], readOnly = false }) => {
  const [forecastModels, setForecastModels] = useState([]);
  const [selectionProcedures, setSelectionProcedures] = useState([]);
  const [periodIndicators, setPeriodIndicators] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);

  const forecastingData = formData.forecasting || {
    plant_id: '',
    forecast_model_id: '',
    hist_periods: '',
    forecast_periods: '',
    tracking_limit: '',
    selection_procedure_id: '',
    period_indicator: '',
    period_ind_shelf_life: ''
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [
        forecastModelsRes,
        selectionProceduresRes,
        periodIndicatorsRes,
        plantsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/forecast-models`),
        axios.get(`${BACKEND_URL}/api/selection-procedures`),
        axios.get(`${BACKEND_URL}/api/period-indicators`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`)
      ]);

      setForecastModels(forecastModelsRes.data || []);
      setSelectionProcedures(selectionProceduresRes.data || []);
      setPeriodIndicators(periodIndicatorsRes.data || []);
      setPlants(plantsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      forecasting: {
        ...prev.forecasting,
        [field]: value
      }
    }));
  };

  const getAvailablePlants = () => {
    if (selectedPlants.length === 0) {
      return plants;
    }
    return plants.filter(p => selectedPlants.includes(p.plant_id));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
        <p className="text-sm text-gray-600">Loading forecasting master data...</p>
      </div>
    );
  }

  const availablePlants = getAvailablePlants();
  const plantConsistencyWarning = selectedPlants.length > 0;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">Forecasting Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure demand forecasting parameters for this material
        </p>
      </div>

      {/* Plant Consistency Warning */}
      {plantConsistencyWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-orange-900">Plant Consistency Enforced</h4>
            <p className="text-sm text-orange-700 mt-1">
              Plant selection is restricted to plants already selected in other views ({selectedPlants.join(', ')}).
            </p>
          </div>
        </div>
      )}

      {/* Plant Selection */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-orange-900 mb-3">Plant *</h4>
        <div>
          <Label>Plant *</Label>
          <select
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            value={forecastingData.plant_id}
            onChange={(e) => handleFieldChange('plant_id', e.target.value)}
          >
            <option value="">Select Plant</option>
            {availablePlants.map(p => (
              <option key={p.plant_id} value={p.plant_id}>
                {p.plant_id} - {p.plant_name}
              </option>
            ))}
          </select>
          {availablePlants.length === 0 && selectedPlants.length > 0 && (
            <p className="text-xs text-red-600 mt-1">No valid plants available. Please check plant selection in other views.</p>
          )}
        </div>
      </div>

      {/* Forecast Model Configuration */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Forecast Model Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Forecast Model *</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={forecastingData.forecast_model_id}
              onChange={(e) => handleFieldChange('forecast_model_id', e.target.value)}
            >
              <option value="">Select Forecast Model</option>
              {forecastModels.map(fm => (
                <option key={fm.forecast_model_id} value={fm.forecast_model_id}>
                  {fm.forecast_model_id} - {fm.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Statistical procedure for demand forecast</p>
          </div>
          <div>
            <Label>Selection Procedure</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={forecastingData.selection_procedure_id}
              onChange={(e) => handleFieldChange('selection_procedure_id', e.target.value)}
            >
              <option value="">Select Procedure (Optional)</option>
              {selectionProcedures.map(sp => (
                <option key={sp.id} value={sp.id}>
                  {sp.id} - {sp.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">For automatic forecast model selection</p>
          </div>
        </div>
      </div>

      {/* Forecast Periods */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Forecast Periods</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Historical Periods</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={forecastingData.hist_periods}
              onChange={(e) => handleFieldChange('hist_periods', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Number of past periods (max 999)</p>
          </div>
          <div>
            <Label>Forecast Periods</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={forecastingData.forecast_periods}
              onChange={(e) => handleFieldChange('forecast_periods', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Number of future periods (max 999)</p>
          </div>
          <div>
            <Label>Tracking Limit</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={forecastingData.tracking_limit}
              onChange={(e) => handleFieldChange('tracking_limit', e.target.value)}
              placeholder="Optional"
            />
            <p className="text-xs text-gray-500 mt-1">Forecast quality monitoring (max 3 digits)</p>
          </div>
        </div>
      </div>

      {/* Period Indicators */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Period Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Period Indicator</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={forecastingData.period_indicator}
              onChange={(e) => handleFieldChange('period_indicator', e.target.value)}
            >
              <option value="">Select Period Indicator (Optional)</option>
              {periodIndicators.map(pi => (
                <option key={pi.period_indicator_id} value={pi.period_indicator_id}>
                  {pi.period_indicator_id} - {pi.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Planning/forecasting period size</p>
          </div>
          <div>
            <Label>Period Indicator for Shelf Life</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={forecastingData.period_ind_shelf_life}
              onChange={(e) => handleFieldChange('period_ind_shelf_life', e.target.value)}
            >
              <option value="">Select Period (Optional)</option>
              <option value="D">D - Days</option>
              <option value="M">M - Months</option>
              <option value="W">W - Weeks</option>
              <option value="Y">Y - Years</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Minimum remaining shelf life unit</p>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-900 mb-1">📋 Note:</p>
        <p>
          Fields marked with * are mandatory. Forecasting data helps in predicting future material demand
          based on historical consumption patterns. Configure the forecast model and periods based on
          your business requirements.
        </p>
      </div>
    </div>
  );
};

export default ForecastingView;
